import { api } from './apiClient';
import type { PreflightKind, SourceName } from '@/types/streaming';
import type { SportsProviderName } from '@/types/sports';
import type { LogDetail, LogQuery, LogsResponse, LogSource } from '@/types/logs';

export interface ConstantMeta {
  key: string;
  section: 'Player' | 'History' | 'Lobby';
  type: 'enum';
  options: number[];
  default: number;
  description: string;
}

export interface ConstantsResponse {
  values: Record<string, number>;
  registry: ConstantMeta[];
}

export interface AccessCode {
  id: string;
  code: string;
  label: string | null;
  createdAt: string;
  redemptionCount: number;
  lastRedeemedAt: string | null;
  uniqueUserCount: number;
  uniqueClientCount: number;
  /** Redeemers become recruiters + are never paywalled. */
  recruiterGrant: boolean;
  /** Redeemers get free, never-paywalled access. */
  freeToUse: boolean;
  /** Set for personal referral codes owned by a recruiter; null for admin codes. */
  ownerUserId: string | null;
  /** Recruiter commission fraction copied to the redeemer (recruiter-grant codes only); null when unset. */
  commissionRate: number | null;
}

export interface Redemption {
  id: string;
  userId: string | null;
  ipAddress: string;
  userAgent: string;
  redeemedAt: string;
}

export interface AdminRecruiter {
  id: string;
  email: string;
  displayName: string | null;
  /** Raw fraction 0..1; null = falls back to the platform default rate. */
  recruiterCommissionRate: number | null;
  recruitedCount: number;
  earnedCents: number;
  withdrawnCents: number;
  balanceCents: number;
  planCount: number;
  createdAt: string;
}

/**
 * Latest health-probe result for one streaming provider. The backend cron
 * upserts this hourly; the admin Service Health panel surfaces the same rows
 * and lets the admin re-trigger a probe via the per-row Test button.
 */
export interface ProviderHealthRow {
  providerName: SourceName;
  ok: boolean;
  kind: PreflightKind | null;
  message: string | null;
  latencyMs: number | null;
  checkedAt: string;
}

export type PlaybackStage = 'navigate' | 'manifest' | 'first_frame' | 'timeout' | 'error';

export interface PlaybackHealthRow {
  providerName: SourceName;
  ok: boolean;
  stage: PlaybackStage;
  message: string | null;
  firstFrameMs: number | null;
  artifactUrl: string | null;
  ranAt: string;
}

export interface ProbeTokenResponse {
  token: string;
  expiresAt: string;
}

/**
 * Latest canary-probe result for one sports provider. Mirrors
 * ProviderHealthRow but the timestamp field is `testedAt` (matching the
 * SportsHealthCheck Prisma model, separate from ProviderHealthCheck).
 */
export type SportsVodResolveStatus = 'fresh' | 'pending' | 'failed' | 'unwatchable';

export interface SportsVodResolveCacheRow {
  sportsVodTitleId: string;
  status: SportsVodResolveStatus;
  dmIds: string[];
  iframeUrl: string | null;
  cachedDataProviderHtml: string | null;
  resolvedAt: string | null;
  failCount: number;
  lastError: string | null;
  lastAttemptedAt: string | null;
}

export interface SportsHealthRow {
  providerName: SportsProviderName;
  ok: boolean;
  kind: string | null;
  message: string | null;
  latencyMs: number | null;
  testedAt: string;
}

// Spending snapshot — mirror of backend's SpendingResponse. v1 estimates
// Fly.io hosting cost from machine config × pricing × MTD uptime; egress is
// intentionally not modeled (Fly's REST API doesn't expose per-machine
// bandwidth counters). See `src/services/spend/spendSummary.service.ts`.
export interface SpendingMachineBreakdown {
  cpuUsd: number;
  memoryUsd: number;
  volumesUsd: number;
  ipv4Usd: number;
}

export interface MachineSpendRow {
  machineId: string;
  region: string;
  state: string;
  cpuKind: string;
  cpus: number;
  memoryMb: number;
  createdAt: string;
  uptimeSecMtd: number;
  // null when cpuKind isn't in the pricing table (UI renders "—" + tooltip).
  mtdCostUsd: number | null;
  projectedMonthlyUsd: number | null;
  breakdown: SpendingMachineBreakdown;
}

export type AppSpendStatus = 'deployed' | 'suspended' | 'pending' | 'unknown';

export interface AppSpendRow {
  appName: string;
  appStatus: AppSpendStatus;
  region: string | null;
  machines: MachineSpendRow[];
  mtdCostUsd: number;
  projectedMonthlyUsd: number;
  hasUnknownMachineTypes: boolean;
  // Set iff per-app machine listing failed; row renders an inline error and
  // the other apps render normally.
  fetchError?: string;
}

export interface SpendingResponse {
  asOf: string;
  cachedUntil: string;
  pricingTableVersion: string;
  apps: AppSpendRow[];
  totals: {
    mtdCostUsd: number;
    projectedMonthlyUsd: number;
    hasUnknownMachineTypes: boolean;
  };
}

export const adminApi = {
  listAccessCodes(): Promise<{ codes: AccessCode[] }> {
    return api<{ codes: AccessCode[] }>('/api/admin/access-codes', { auth: true });
  },
  createAccessCode(input: {
    label?: string;
    code?: string;
    recruiterGrant?: boolean;
    freeToUse?: boolean;
    commissionRate?: number;
  }): Promise<{ code: AccessCode }> {
    return api<{ code: AccessCode }>('/api/admin/access-codes', {
      method: 'POST',
      body: input,
      auth: true,
    });
  },
  deleteAccessCode(id: string): Promise<null> {
    return api<null>(`/api/admin/access-codes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      auth: true,
    });
  },
  listRedemptions(codeId: string): Promise<{ redemptions: Redemption[] }> {
    return api<{ redemptions: Redemption[] }>(
      `/api/admin/access-codes/${encodeURIComponent(codeId)}/redemptions`,
      { auth: true },
    );
  },

  // ---- Recruiters (admin management) ----
  listRecruiters(): Promise<{ recruiters: AdminRecruiter[] }> {
    return api<{ recruiters: AdminRecruiter[] }>('/api/admin/recruiters', { auth: true });
  },
  setRecruiterCommission(id: string, rate: number): Promise<{ recruiter: { id: string; recruiterCommissionRate: number } }> {
    return api<{ recruiter: { id: string; recruiterCommissionRate: number } }>(
      `/api/admin/recruiters/${encodeURIComponent(id)}/commission`,
      { method: 'PUT', body: { rate }, auth: true },
    );
  },

  /**
   * Constants are exposed publicly (the frontend pulls before login) so this
   * sits on /api/constants, not /api/admin. Writes still go through
   * /api/admin/constants/:key below and are admin-gated.
   */
  getConstants(): Promise<ConstantsResponse> {
    return api<ConstantsResponse>('/api/constants');
  },
  updateConstant(key: string, value: number): Promise<{ key: string; value: number }> {
    return api<{ key: string; value: number }>(
      `/api/admin/constants/${encodeURIComponent(key)}`,
      {
        method: 'PUT',
        body: { value },
        auth: true,
      },
    );
  },

  // ---- Service Health ----
  listHealthChecks(): Promise<{ checks: ProviderHealthRow[] }> {
    return api<{ checks: ProviderHealthRow[] }>('/api/admin/health/providers', { auth: true });
  },
  testProvider(name: SourceName): Promise<{ check: ProviderHealthRow }> {
    return api<{ check: ProviderHealthRow }>(
      `/api/admin/health/providers/${encodeURIComponent(name)}/test`,
      { method: 'POST', auth: true },
    );
  },
  testAllProviders(): Promise<{ checks: ProviderHealthRow[] }> {
    return api<{ checks: ProviderHealthRow[] }>('/api/admin/health/providers/test-all', {
      method: 'POST',
      auth: true,
    });
  },

  // ---- Sports VOD Resolve Cache ----
  listSportsVodResolveCache(opts: { status?: SportsVodResolveStatus; limit?: number } = {}): Promise<{ rows: SportsVodResolveCacheRow[] }> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return api<{ rows: SportsVodResolveCacheRow[] }>(`/api/admin/sports-vod-resolve-cache${qs ? `?${qs}` : ''}`, { auth: true });
  },
  reResolveSportsVodTitle(titleId: string): Promise<{ status: 'enqueued'; titleId: string }> {
    return api<{ status: 'enqueued'; titleId: string }>(
      `/api/admin/sports-vod-resolve-cache/${encodeURIComponent(titleId)}/re-resolve`,
      { method: 'POST', auth: true },
    );
  },
  unhideSportsVodTitle(titleId: string): Promise<{ row: SportsVodResolveCacheRow }> {
    return api<{ row: SportsVodResolveCacheRow }>(
      `/api/admin/sports-vod-resolve-cache/${encodeURIComponent(titleId)}/unhide`,
      { method: 'POST', auth: true },
    );
  },

  // ---- Sports Health ----
  listSportsHealthChecks(): Promise<{ checks: SportsHealthRow[] }> {
    return api<{ checks: SportsHealthRow[] }>('/api/admin/sports-health/providers', { auth: true });
  },
  testSportsProvider(name: SportsProviderName): Promise<{ check: SportsHealthRow }> {
    return api<{ check: SportsHealthRow }>(
      `/api/admin/sports-health/${encodeURIComponent(name)}/test`,
      { method: 'POST', auth: true },
    );
  },
  testAllSportsProviders(): Promise<{ checks: SportsHealthRow[] }> {
    return api<{ checks: SportsHealthRow[] }>('/api/admin/sports-health/test-all', {
      method: 'POST',
      auth: true,
    });
  },

  // ---- Playback Health (end-to-end probe from GH Actions) ----
  listPlaybackHealth(): Promise<{ rows: PlaybackHealthRow[] }> {
    return api<{ rows: PlaybackHealthRow[] }>('/api/admin/playback-health/results', { auth: true });
  },

  // ---- Probe Token (mint a long-lived JWT for the GH Actions probe) ----
  mintProbeToken(): Promise<ProbeTokenResponse> {
    return api<ProbeTokenResponse>('/api/admin/probe-token', { method: 'POST', auth: true });
  },

  // ---- Logs (unified Sentry + PostHog feed) ----
  listLogs(query: LogQuery): Promise<LogsResponse> {
    const params = new URLSearchParams();
    params.set('source', query.source);
    params.set('since', query.since);
    params.set('sort', query.sort);
    for (const v of query.provider) params.append('provider', v);
    for (const v of query.kind) params.append('kind', v);
    for (const v of query.level) params.append('level', v);
    if (query.q.trim().length > 0) params.set('q', query.q);
    if (query.bust) params.set('bust', '1');
    return api<LogsResponse>(`/api/admin/logs?${params.toString()}`, { auth: true });
  },

  getLogDetail(source: LogSource, id: string): Promise<LogDetail> {
    return api<LogDetail>(
      `/api/admin/logs/${encodeURIComponent(source)}/${encodeURIComponent(id)}`,
      { auth: true },
    );
  },

  // ---- Spending (estimated Fly.io hosting cost per machine) ----
  getSpending(): Promise<SpendingResponse> {
    return api<SpendingResponse>('/api/admin/spending', { auth: true });
  },
  refreshSpending(): Promise<SpendingResponse> {
    return api<SpendingResponse>('/api/admin/spending/refresh', { method: 'POST', auth: true });
  },
};
